import { useCallback, useEffect, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../components/recharts/functions";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../models/chart_model";
import { renderHourTick } from "../../../../components/recharts/components";
import { useSelector } from "react-redux";
import { R_CHART_TYPES } from "../../const";
import loader from "../../../../components/recharts/loader.gif";
import { normalizeDisplayLabelWithoutUndefined } from "../../../../utility/displayText";
import { fetchModSimulationCostPower } from "../../Utility/RestApi";
interface CostData {
  date: string;
  time_slot: number;
  purchase_mw: number;
  generator: string;
  purchase_price: number;
  demand_mw: number;
  must_run_mw: number;
  must_run_price: number;
  gen_mw: number;
  gen_cost: number;
}

export default function CostPowerSimulationAnalysis({
  refreshKey,
  selectedBlock,
  selectedGeneratorName,
  setError,
}: {
  refreshKey: number;
  selectedBlock: any[];
  selectedGeneratorName: any[];
  setError?: any;
}) {
  const [data, setData] = useState<CostData[]>([]);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const chartSuffixes = [
    {
      suffix: "gen_mw",
      displayName: "Withdrawable Capacity (MW)",
      stroke: "rgba(252, 233, 222, 1)",
      legendColor: "rgba(255, 194, 158, 1)",
      stackSuffix: "gen_mw",
      chartType: ChartType.Bar,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "must_run_mw",
      displayName: "Must Run (MW)",
      stroke: "rgba(241, 147, 92, 1)",
      stackSuffix: "must_run_mw",
      chartType: ChartType.Bar,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "purchase_mw",
      displayName: "Purchase Quantum Dam (MW)",
      stroke: "rgba(255, 163, 163, 1)",
      stackSuffix: "purchase_mw",
      chartType: ChartType.Bar,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "gen_cost",
      displayName: "Withdrawable Capacity Cost (₹)",
      stroke: "rgba(52, 101, 109, 1)",
      stackSuffix: "gen_cost",
      chartType: ChartType.Line,
      unit: "₹",
      yAxisId: "right",
    },
    {
      suffix: "must_run_price",
      displayName: "Must Run Cost (₹)",
      stroke: "rgba(0, 191, 111, 1)",
      stackSuffix: "must_run_price",
      chartType: ChartType.Line,
      unit: "₹",
      yAxisId: "right",
    },
    {
      suffix: "purchase_price",
      displayName: "Purchase Quantum Dam Cost (₹)",
      stroke: "rgba(17, 141, 255, 1)",
      stackSuffix: "purchase_price",
      chartType: ChartType.Line,
      unit: "₹",
      yAxisId: "right",
    },
  ];

  const [selectedMetrics] = useState<any[]>([
    chartSuffixes[0],
    chartSuffixes[1],
    chartSuffixes[2],
    chartSuffixes[3],
    chartSuffixes[4],
    chartSuffixes[5],
  ]);
  const [selectedChartType] = useState(R_CHART_TYPES[1]);
  const preSelectedDate = useSelector(
    (state: any) => state.modGridcoDate.selectedDate
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modGridcoSession.modSession
  );

  const handleFetchData = useCallback(async () => {
    if (!preSelectedDate || !modSelectedSession) return;

    try {
      setLoading(true);
      const res = await fetchModSimulationCostPower(
        preSelectedDate,
        modSelectedSession
      );
      setData(res.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("An error occurred while fetching data.");
    } finally {
      setLoading(false);
    }
  }, [modSelectedSession, preSelectedDate, setError]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);

  useEffect(() => {
    let filteredData = data.map((item) => ({
      ...item,
      date: preSelectedDate,
    }));
    if (selectedBlock.length > 0) {
      const blocks = selectedBlock.map((b: any) => Number(b.value ?? b));
      filteredData = filteredData.filter((row) =>
        blocks.includes(Number(row.time_slot))
      );
    }
    if (selectedGeneratorName.length > 0) {
      const generator = selectedGeneratorName.map((o: any) => o.value ?? o);
      filteredData = filteredData.filter((row) =>
        generator.includes(row.generator)
      );
    }
    const par: ChartDataParams = {
      keys: ["date", "time_slot"],
      tableKeys: ["date", "time_slot", "generator"],
      groupBy: [""],
      volumeKey: ``,
      aggregationKeys: [
        "must_run_mw",
        "gen_mw",
        "gen_cost",
        "purchase_mw",
        "must_run_price",
        "purchase_price",
      ],
      average: true,
      averageKeys: [],
      averageDuplicationKeys: ["date", "time_slot", "generator"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [data, preSelectedDate, selectedBlock, selectedGeneratorName]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    stroke: string,
    legendColor: string,
    yAxisId: string
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
      type: ["gen_cost", "purchase_price", "must_run_price"].includes(suffix)
        ? ChartType.Line
        : ChartType.Bar,
      unit: unit,
      yAxisId: yAxisId,
      stroke: stroke,
      legendColor: legendColor,
      stackId:
        selectedChartType.stackId === undefined ? stackSuffix : undefined,
    };
  };

  function getLegends() {
    return selectedMetrics.flatMap(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        unit,
        yAxisId,
        stroke,
        legendColor,
      }) =>
        chartKeys.map(
          (key, index) =>
            createChartObject(
              key,
              index,
              suffix,
              displayName,
              stackSuffix,
              chartType,
              unit,
              stroke,
              legendColor,
              yAxisId
            ) as any
        )
    );
  }

  return (
    <>
      <div className="dash-card col-span-1 md:col-span-12 border border-[#718EBF1C] !bg-[#fff] !shadow-[rgba(149,157,165,0.2)_0px_8px_24px] !min-h-[100%] w-full">
        <div className="flex w-full justify-center items-center">
          <h2 className="text-center chartHeading w-full ">
            Cost of Power (DAM)
          </h2>
        </div>
        {loading ? (
          <div className="h-full w-full bg-white flex items-center justify-center">
            <img
              src={loader}
              width={200}
              alt="loader"
              className="block mt-[5rem]"
            />
          </div>
        ) : (
          <>
            {chartData.length === 0 ? (
              <>
                <div className=" flex items-center justify-center overflow-auto capitalize text-nowrap rounded-lg w-full h-full">
                  <h1 className="chartHeading !text-base text-center !font-medium w-100">
                    No Cost of Power Available...
                  </h1>
                </div>
              </>
            ) : (
              <>
                <ReMixChart
                  chartHeight="calc(100% - 50px)"
                  data={chartData}
                  showBrush={false}
                  brushStart={BrushStart.Start}
                  isTimeSlot={true}
                  onlyTitle={true}
                  legends={getLegends()}
                  yAxisLabel="MW"
                  secondYAxisLabel="₹"
                  nameGap={40}
                  xDataKey={"time_slot"}
                  top={10} // Top margin for grid
                  bottom={30} // Bottom margin for grid
                  xTick={(value: any, index: any) => {
                    return renderHourTick(value, 10);
                  }}
                  secondXDataKey={"date"}
                  showToolbox={false}
                />
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
